import { StandardEvents, CartLinesUpdateEvent } from '@shopify/events';

/**
 * Nube : tiroir panier.
 * - window.nubeCartDrawer.open(addResponse) : rafraîchit le tiroir après un ajout
 *   fait par le thème (bloc nube-tiers) et l'ouvre. Renvoie false si ce n'est pas
 *   possible : l'appelant redirige alors vers la page panier.
 * - Minuteur « Your cart is reserved for » : 10 minutes, mémorisées dans le
 *   navigateur ; remis à 10 minutes à chaque ajout, effacé quand le panier est vide.
 *   Si les 10 minutes sont écoulées alors que le tiroir est fermé, le minuteur repart
 *   à 10:00 (sinon un ancien délai périmé masquait le décompte).
 * - Clic sur le fond assombri (PC) : ferme le tiroir.
 * - Email « Get my offer » : applique WELCOME10 (10 %) et WELCOMESHIP (livraison offerte au UK, quel que soit
 *   le nombre d'articles) dans le tiroir, en un seul appel panier, sans quitter le panier, et envoie l'email
 *   à Klaviyo (identify + évènement), sans rechargement. L'envoi au formulaire client de Shopify est
 *   abandonné : il est protégé par hCaptcha (« Missing CAPTCHA token ») et force le rechargement.
 *   L'email est aussi gardé en attribut du panier « Welcome email » et en checkout[email].
 *   Consentement marketing : la phrase affichée sous le champ informe le client. En envoyant son email il accepte
 *   les emails marketing : on l'enregistre (1) en abonnant l'email à la liste Klaviyo indiquée dans
 *   data-klaviyo-list du bouton (API client Klaviyo « subscriptions », consentement email), si la liste est
 *   renseignée, et (2) dans l'attribut du panier « Marketing consent » (visible sur la commande).
 *   L'attribut du panier « Free delivery code » = WELCOMESHIP fait afficher la livraison offerte dans les totaux
 *   (snippets/nube-cart-delivery et cart-summary) : Liquid ne voit pas les remises de livraison.
 * - Croix du code dans les totaux : déclenche la croix (masquée) du composant discount du thème.
 */

const STORAGE_KEY = 'nube-cart-reserved-until';
const DURATION_MS = 10 * 60 * 1000;
const KLAVIYO_COMPANY_ID = 'RKk6yx';

const store = {
  get() {
    try {
      return Number(localStorage.getItem(STORAGE_KEY)) || 0;
    } catch (e) {
      return 0;
    }
  },
  set(value) {
    try {
      localStorage.setItem(STORAGE_KEY, String(value));
    } catch (e) {}
  },
  clear() {
    try {
      localStorage.removeItem(STORAGE_KEY);
    } catch (e) {}
  },
};

const pad = (n) => String(n).padStart(2, '0');

function tick() {
  const timers = document.querySelectorAll('[data-nube-timer]');
  if (!timers.length) {
    if (document.querySelector('.cart-drawer--empty')) store.clear();
    return;
  }

  const drawer = document.getElementById('cart-drawer');
  const isOpen = Boolean(drawer && drawer.hasAttribute('open'));

  let until = store.get();
  if (!until || (until <= Date.now() && !isOpen)) {
    until = Date.now() + DURATION_MS;
    store.set(until);
  }

  const left = until - Date.now();
  const expired = left <= 0;
  const seconds = Math.max(0, Math.ceil(left / 1000));
  const label = pad(Math.floor(seconds / 60)) + ':' + pad(seconds % 60);

  timers.forEach((timer) => {
    timer.classList.toggle('is-expired', expired);
    const value = timer.querySelector('[data-nube-timer-value]');
    if (value && !expired && value.textContent !== label) value.textContent = label;
  });
}

if (!window.__nubeCartTimer) {
  window.__nubeCartTimer = setInterval(tick, 1000);

  document.addEventListener(StandardEvents.cartLinesUpdate, (event) => {
    if (event.action === 'add') store.set(Date.now() + DURATION_MS);
  });

  document.addEventListener('click', (event) => {
    const drawer = document.getElementById('cart-drawer');

    // Croix du code de réduction dans les totaux
    const removeButton = event.target instanceof Element ? event.target.closest('[data-nube-remove-code]') : null;
    if (removeButton) {
      event.preventDefault();
      const code = removeButton.getAttribute('data-nube-remove-code');
      const pills = document.querySelectorAll('.cart-discount__pill');
      for (const pill of pills) {
        if (pill.getAttribute('data-discount-code') === code) {
          const themeRemove = pill.querySelector('.cart-discount__pill-remove');
          if (themeRemove) themeRemove.click();
          break;
        }
      }
      return;
    }

    // Fond assombri (PC) : un clic dessus tombe sur <body>
    if (!drawer || !drawer.hasAttribute('open')) return;
    if (event.target === document.body || event.target === document.documentElement) {
      if (typeof drawer.close === 'function') drawer.close();
    }
  });

  // Formulaire email : jamais envoyé à Shopify, traité ici (voir handleWelcome)
  document.addEventListener(
    'submit',
    (event) => {
      const form = event.target;
      if (form instanceof HTMLFormElement && form.classList.contains('nube-welcome')) {
        event.preventDefault();
        event.stopImmediatePropagation();
        handleWelcome(form);
      }
    },
    true
  );
  // Le tiroir est re-généré après chaque mise à jour : on remet l'email juste avant le checkout
  document.addEventListener(
    'submit',
    (event) => {
      const form = event.target;
      if (!(form instanceof HTMLFormElement) || !form.querySelector('[name="checkout"]')) return;
      let email = '';
      try { email = sessionStorage.getItem('nube-welcome-email') || ''; } catch (e) {}
      if (email) fillCheckoutEmail(form, email);
    },
    true
  );
}
tick();

function fillCheckoutEmail(cartForm, email) {
  let hidden = cartForm.querySelector('input[name="checkout[email]"]');
  if (!hidden) {
    hidden = document.createElement('input');
    hidden.type = 'hidden';
    hidden.name = 'checkout[email]';
    cartForm.appendChild(hidden);
  }
  hidden.value = email;
}

// Klaviyo (bloc d'application « klaviyo-onsite-embed » activé dans le thème) :
// la file _learnq est lue dès que klaviyo.js est chargé.
// Si une liste est indiquée (listId), l'email est aussi abonné à cette liste avec le consentement email
// (API client Klaviyo, clé publique = ID de la boutique Klaviyo ; aucune clé secrète).
function sendToKlaviyo(email, shipCode, listId) {
  window._learnq = window._learnq || [];
  window._learnq.push(['identify', { $email: email, 'Signup source': 'Cart drawer - free delivery + 10% first order' }]);
  window._learnq.push(['track', 'Cart Welcome 10 Signup', { email: email, code: 'WELCOME10', free_delivery_code: shipCode || '', page: window.location.pathname }]);

  if (!listId) return Promise.resolve(false);
  return fetch('https://a.klaviyo.com/client/subscriptions/?company_id=' + encodeURIComponent(KLAVIYO_COMPANY_ID), {
    method: 'POST',
    headers: { 'Content-Type': 'application/json', revision: '2024-10-15' },
    body: JSON.stringify({
      data: {
        type: 'subscription',
        attributes: {
          custom_source: 'Cart drawer - free delivery + 10% first order',
          profile: { data: { type: 'profile', attributes: { email: email } } },
        },
        relationships: { list: { data: { type: 'list', id: listId } } },
      },
    }),
  })
    .then((r) => r.ok)
    .catch(() => false);
}

const EMAIL_PATTERN = /^[^\s@]+@[^\s@]+\.[^\s@]{2,}$/;

// Re-génère la section du panier (tiroir ou page) depuis le serveur, comme le fait le composant de réduction du thème.
async function refreshCartSection() {
  try {
    const component = document.querySelector('cart-discount-component');
    const sectionId = component && component.dataset.sectionId;
    if (!sectionId) return;
    const { sectionRenderer } = await import('@theme/section-renderer');
    await sectionRenderer.renderSection(sectionId, {
      cache: false,
      mode: component.closest('theme-drawer') ? 'hydration' : 'full',
    });
  } catch (e) {
    console.warn('[nube-cart-drawer] refresh', e);
  }
}

async function handleWelcome(form) {
  const input = form.querySelector('.nube-welcome__input');
  const button = form.querySelector('.nube-welcome__button');
  const message = form.querySelector('.nube-welcome__msg');
  const code = (button && button.dataset.code) || 'WELCOME10';
  const shipCode = (button && button.dataset.shipCode) || '';
  const listId = (button && button.dataset.klaviyoList) || '';
  const say = (text, isError) => {
    message.textContent = text;
    message.classList.toggle('is-error', Boolean(isError));
  };

  const email = input.value.trim();
  if (!EMAIL_PATTERN.test(email)) {
    say('Please enter a valid email address.', true);
    return;
  }

  button.disabled = true;
  say('');

  try {
    const root = (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || '/';
    const post = (body) =>
      fetch(root + 'cart/update.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify(body),
      }).then((r) => r.json());
    const readCart = () => fetch(root + 'cart.js', { headers: { Accept: 'application/json' } }).then((r) => r.json());
    const isOn = (cart, wanted) => (cart.discount_codes || []).some((d) => d.code === wanted && d.applicable);

    // Email : Klaviyo (abonnement + consentement email si une liste est indiquée), et pré-remplissage du checkout (paniers abandonnés)
    sendToKlaviyo(email, shipCode, listId);
    document.querySelectorAll('form').forEach((f) => { if (f.querySelector('[name="checkout"]')) fillCheckoutEmail(f, email); });
    try { sessionStorage.setItem('nube-welcome-email', email); } catch (e) {}

    // Trace du consentement, gardée avec la commande
    const consent = 'Agreed to marketing emails in cart drawer on ' + new Date().toISOString();

    const before = await readCart();
    if (isOn(before, code) && (!shipCode || isOn(before, shipCode))) {
      await post({ attributes: { 'Welcome email': email, 'Marketing consent': consent } });
      say('Your free delivery and 10% are already applied.');
      return;
    }

    // Un seul appel : l'email, les codes déjà présents + les 2 codes d'accueil, et l'attribut qui fait
    // afficher la livraison offerte dans les totaux.
    const existing = (before.discount_codes || []).map((d) => d.code);
    const codes = Array.from(new Set(existing.concat([code], shipCode ? [shipCode] : [])));
    const attributes = { 'Welcome email': email, 'Marketing consent': consent };
    if (shipCode) attributes['Free delivery code'] = shipCode;
    await post({ discount: codes.join(','), attributes: attributes });

    const after = await readCart();
    const percentOk = isOn(after, code);
    const shipOk = !shipCode || isOn(after, shipCode);

    // Si la livraison offerte n'a pas pu être appliquée : on retire l'attribut (sinon les totaux la montreraient offerte)
    if (shipCode && !shipOk) await post({ attributes: { 'Free delivery code': '' } });

    await refreshCartSection();

    if (percentOk && shipOk) say(shipCode ? 'Your free delivery and 10% are applied.' : 'Your 10% is applied.');
    else if (percentOk) say('Your 10% is applied. Free delivery could not be applied to this cart.', true);
    else say('Your offer could not be applied to this cart.', true);
  } catch (error) {
    say('Something went wrong, please try again.', true);
  } finally {
    button.disabled = false;
  }
}

async function open(addResponse) {
  try {
    const root = (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || '/';
    const drawer = document.getElementById('cart-drawer');
    if (!drawer || typeof drawer.open !== 'function') return false;

    const cart = await fetch(root + 'cart.js', { headers: { Accept: 'application/json' } }).then((r) => r.json());
    const added = (addResponse && addResponse.items) || [];
    const deferred = CartLinesUpdateEvent.createPromise();

    document.dispatchEvent(
      new CartLinesUpdateEvent({
        action: 'add',
        context: 'product',
        lines: added.map((item) => ({ merchandiseId: String(item.variant_id), quantity: item.quantity })),
        promise: deferred.promise,
      })
    );

    deferred.resolve({
      cart: CartLinesUpdateEvent.createCartFromAjaxResponse(cart),
      detail: {
        items: cart.items,
        sections: addResponse && addResponse.sections,
        itemCount: cart.item_count,
        didError: false,
        source: 'nube-tiers',
      },
    });

    drawer.open();
    tick();
    return true;
  } catch (error) {
    console.warn('[nube-cart-drawer]', error);
    return false;
  }
}

window.nubeCartDrawer = { open };
